import { useQuery } from '@tanstack/react-query';
import { differenceInDays, format, parseISO } from 'date-fns';
import { supabase } from '@/lib/supabase';
import { autoGranularity, zeroFillBuckets, type SparseBucket } from '@/lib/analytics/buckets';
import type { Granularity } from '@/lib/analytics/metrics';

interface RPCRow {
  period_label: 'current' | 'previous';
  bucket_date: string;
  value: number;
  segment?: string;
}

export interface TimeSeriesBucket {
  bucket_date: string;
  current: number;
  previous: number | null;
}

export interface TimeSeriesResult {
  kind: 'time-series';
  buckets: TimeSeriesBucket[];
  total_current: number;
  total_previous: number | null;
  delta_pct: number | null;
  granularity: Granularity;
  isLoading: boolean;
  error: Error | null;
}

interface UseTimeseriesArgs {
  rpcName: string;
  args: Record<string, unknown>;
  queryKey: readonly unknown[];
  /** Current window start (inclusive). */
  from: Date;
  /** Current window end (exclusive). */
  to: Date;
  /** Previous window start (inclusive); null when comparison is disabled. */
  compareFrom: Date | null;
  /** Previous window end (exclusive); null when comparison is disabled. */
  compareTo: Date | null;
  granularity: Granularity;
  enabled?: boolean;
}

/**
 * Shared primitive for the per-metric time-series hooks. Owns the React Query
 * setup, RPC call, and result-shape normalization.
 *
 * Both the current and previous axes are zero-filled independently, then merged
 * by positional index. After zero-fill they have equal length (per
 * resolveCompareRange) so position-i of the current axis aligns with position-i
 * of the previous axis on the rendered x-axis.
 *
 * isLoading + empty buckets means "fetching"; isLoading: false + empty means
 * "loaded zero results" — distinguish via isLoading, not bucket count.
 */
export function useTimeseriesQuery(opts: UseTimeseriesArgs): TimeSeriesResult {
  // Force-disable compare for hour granularity — positional bucket merge
  // would misalign on DST 23/25-hour days.
  const hourMode = opts.granularity === 'hour';
  const effCompareFrom = hourMode ? null : opts.compareFrom;
  const effCompareTo = hourMode ? null : opts.compareTo;
  const comparisonEnabled = effCompareFrom !== null && effCompareTo !== null;

  // Replace null compare args inside the RPC payload too, so the server
  // returns only current-period rows.
  const rpcArgs = hourMode
    ? { ...opts.args, p_compare_from: null, p_compare_to: null }
    : opts.args;

  const { data, isLoading, error } = useQuery({
    queryKey: opts.queryKey,
    queryFn: async () => {
      const { data, error } = await supabase.rpc(opts.rpcName, rpcArgs);
      if (error) throw error;
      return (data ?? []) as RPCRow[];
    },
    enabled: opts.enabled ?? true,
    staleTime: 60_000,
  });

  // For all granularities, server returns ISO-shaped strings in bucket_date.
  // Re-key by the granularity's bucket-key format before merge.
  const keyFormat = opts.granularity === 'hour' ? "yyyy-MM-dd'T'HH" : 'yyyy-MM-dd';
  const rows = (data ?? []).map((r) => ({
    ...r,
    bucket_date: format(parseISO(r.bucket_date), keyFormat),
  }));

  const currentRows = rows.filter((r) => r.period_label === 'current');
  const previousRows = rows.filter((r) => r.period_label === 'previous');

  const totalCurrent = currentRows.reduce((sum, r) => sum + r.value, 0);
  const totalPrevious = comparisonEnabled
    ? previousRows.reduce((sum, r) => sum + r.value, 0)
    : null;

  const sparseCurrent: SparseBucket[] = currentRows.map((r) => ({
    bucket_date: r.bucket_date,
    current: r.value,
    previous: null,
  }));
  const filledCurrent = zeroFillBuckets(sparseCurrent, opts.from, opts.to, opts.granularity);

  let filledPrevious: SparseBucket[] | null = null;
  if (comparisonEnabled && effCompareFrom && effCompareTo) {
    const sparsePrev: SparseBucket[] = previousRows.map((r) => ({
      bucket_date: r.bucket_date,
      current: r.value,
      previous: null,
    }));
    filledPrevious = zeroFillBuckets(sparsePrev, effCompareFrom, effCompareTo, opts.granularity);
  }

  const buckets: TimeSeriesBucket[] = filledCurrent.map((b, i) => ({
    bucket_date: b.bucket_date,
    current: b.current,
    previous: filledPrevious ? (filledPrevious[i]?.current ?? null) : null,
  }));

  const deltaPct =
    totalPrevious === null ? null
      : totalPrevious === 0 && totalCurrent === 0 ? 0
      : totalPrevious === 0 ? null
      : Math.round(((totalCurrent - totalPrevious) / totalPrevious) * 1000) / 10;

  return {
    kind: 'time-series',
    buckets,
    total_current: totalCurrent,
    total_previous: totalPrevious,
    delta_pct: deltaPct,
    granularity: opts.granularity,
    isLoading,
    error: (error as Error) ?? null,
  };
}

/**
 * Resolve the granularity to use for a date range. If `manual === 'auto'`,
 * use the auto rule from `buckets.autoGranularity`; otherwise pass-through.
 */
export function computeGranularity(
  from: Date,
  to: Date,
  manual: 'auto' | Granularity,
): Granularity {
  if (manual !== 'auto') return manual;
  return autoGranularity(differenceInDays(to, from));
}
